import {cva} from 'class-variance-authority'
import {ReactElement, ReactNode} from 'react'

import {AlertCircleIcon, CheckCircleIcon, InfoCircleIcon, XCloseIcon} from '@components/ui/icon'
import {Container} from '@components/ui/container'
import {cn} from '@utilities/helpers'

export type Sentiment = 'info' | 'warning' | 'error' | 'errorStrong' | 'success'

const alertVariants = cva('flex w-full justify-center border-b border-neutral-200 [&_b]:font-medium', {
    variants: {
        sentiment: {
            info: 'bg-neutral-50 [&_p]:text-neutral-700',
            warning: 'bg-warning-50 [&_p]:text-neutral-900',
            error: 'bg-danger-50 [&_p]:text-neutral-700',
            errorStrong: 'bg-danger-100 [&_p]:text-neutral-700',
            success: 'bg-success-50 [&_p]:text-neutral-700'
        }
    },
    defaultVariants: {
        sentiment: 'info'
    }
})

const iconToComponent = {
    info: <InfoCircleIcon className="text-neutral-600" size={20} />,
    warning: <AlertCircleIcon className="text-warning-600" size={20} />,
    error: <AlertCircleIcon className="text-danger-600" size={20} />,
    errorStrong: <AlertCircleIcon className="text-danger-600" size={20} />,
    success: <CheckCircleIcon className="text-success-600" size={20} />
} as const satisfies Record<Sentiment, ReactNode>

interface AlertProps {
    sentiment?: Sentiment
    title?: ReactNode
    description?: ReactNode
    icon?: Sentiment | 'default' | ReactElement
    endSlot?: ReactNode
    onClose?: () => void
    className?: string
}

function Alert({title, description, sentiment = 'info', icon, endSlot, onClose, className}: AlertProps) {
    const hasRightSide = !!endSlot || !!onClose

    return (
        <article className={cn(alertVariants({sentiment}), className)} data-slot="alert">
            <Container
                className={cn(
                    'grid items-center gap-3 py-3 text-sm [&_h1]:font-medium [&_h1]:text-neutral-900',
                    hasRightSide ? 'grid-cols-[minmax(0,1fr)_auto]' : 'grid-cols-[minmax(0,1fr)]'
                )}
            >
                <div className="flex items-center gap-3">
                    {!icon
                        ? undefined
                        : icon === 'default'
                          ? iconToComponent[sentiment]
                          : typeof icon === 'object'
                            ? icon
                            : iconToComponent[icon]}
                    <div className="flex flex-wrap items-baseline gap-1.5">
                        {title && <h1>{title}</h1>}
                        {description && <p>{description}</p>}
                    </div>
                </div>
                {hasRightSide && (
                    <div className="flex items-center gap-3">
                        {!!endSlot && endSlot}
                        {!!onClose && (
                            <button
                                className="flex size-9 cursor-pointer items-center justify-center [&_svg]:size-5 [&_svg]:fill-neutral-400"
                                onClick={onClose}
                                type="button"
                            >
                                <XCloseIcon />
                            </button>
                        )}
                    </div>
                )}
            </Container>
        </article>
    )
}

export {Alert}
